import { fuseDark } from '@fuse/colors';
import _ from '@lodash';
import { lightBlue, red } from '@mui/material/colors';
import { createTheme, adaptV4Theme } from '@mui/material/styles';
import { ExpandMore } from '@mui/icons-material';
import { SortLabelIcon } from 'app/components/Icons';
import qs from 'qs';

/**
 * SETTINGS DEFAULTS
 */
export const defaultSettings = {
	customScrollbars: true,
	animations: true,
	direction: 'ltr',
	theme: {
		main: 'default',
		navbar: 'mainThemeDark',
		toolbar: 'mainThemeLight',
		footer: 'mainThemeDark',
	},
};

export function getParsedQuerySettings() {
	const parsedQueryString = qs.parse(window.location.search, { ignoreQueryPrefix: true });

	if (parsedQueryString && parsedQueryString.defaultSettings) {
		return JSON.parse(parsedQueryString.defaultSettings);
	}
	return {};

	// Generating route params from settings
	/* const settings = qs.stringify({
		defaultSettings: JSON.stringify(defaultSettings, {strictNullHandling: true})
	});
	console.info(settings); */
}

/**
 * THEME DEFAULTS
 */
// DEV NOTE::add your custom theme rules here
export function mustHaveThemeOptions(themeObj) {
	const theme = createTheme(adaptV4Theme(themeObj));
	return {
		breakpoints: {
			values: {
				xs: 0,
				sm: 600,
				md: 960,
				lg: 1280,
				xl: 1920,
			},
		},
		typography: {
			fontFamily: ["'Open Sans'", 'Muli', 'Roboto', '"Helvetica"', 'Arial', 'sans-serif'].join(','),
			fontWeightLight: 300,
			fontWeightRegular: 400,
			fontWeightMedium: 600,
			useNextVariants: true,
			suppressDeprecationWarnings: true,
			htmlFontSize: 10,
			body1: {
				fontSize: '1.4rem',
			},
			body2: {
				fontSize: '1.4rem',
			},
			button: {
				textTransform: 'none',
			},
		},
		props: {
			MuiTableSortLabel: {
				IconComponent: SortLabelIcon,
			},
			MuiPaper: {
				elevation: 8,
			},
			MuiTooltip: {
				disableInteractive: true,
			},
			MuiSelect: {
				IconComponent: ExpandMore,
			},
			/* CHANGED FOR MUI4 TO MATCH MUI5 */
			// the below rules being defaulted to `secondary` isn't preferable - it just makes a smoother transition from Mui4 to 5 (where the default changed)
			MuiCheckbox: {
				color: 'secondary',
			},
			MuiRadio: {
				color: 'secondary',
			},
			MuiSwitch: {
				color: 'secondary',
			},
			MuiTabs: {
				indicatorColor: 'secondary',
				textColor: 'inherit',
			},
			MuiLink: {
				underline: 'hover',
			},
			/* END: CHANGED FOR MUI4 TO MATCH MUI5 */
		},
		overrides: {
			MuiTableSortLabel: {
				icon: {
					fill: '#A48ADB',
					fontSize: '24px',
				},
			},
			MuiTooltip: {
				tooltip: {
					backgroundColor: '#663399',
					fontFamily: 'Open Sans',
					fontSize: 12,
					color: '#ffffff',
					opacity: '0.87 !important',
					borderRadius: 4,
					whiteSpace: 'pre-wrap',
				},
			},
			MuiIconButton: {
				root: {
					color: '#7D61BC',
					borderRadius: 8,
				},
			},
			// keep selected days rounded on calendar
			MuiPickersBasePicker: {
				container: {
					'& .MuiIconButton-root': {
						borderRadius: '50%',
					},
				},
			},
			MuiCheckbox: {
				root: {
					'&:hover': {
						color: theme.palette.secondary.main,
					},
				},
				indeterminate: {
					color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.secondary.main,
				},
			},
			MuiInputLabel: {
				root: {
					'&.Mui-focused': {
						color: 'currentColor',
					},
				},
			},
			MuiTabs: {
				indicator: {
					color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.secondary.main,
					backgroundColor: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.secondary.main,
				},
			},
			MuiTableRow: {
				root: {
					height: '3.2rem !important',
					'& td, th': {
						padding: '4px 16px',
					},
				},
			},
			MuiDialog: {
				container: {
					background: 'rgba(38, 38, 38, 0.39)',
					'[data-tier="enterprise"] & .MuiAppBar-colorPrimary': {
						backgroundColor: theme.palette.mode === 'dark' ? '#515151' : '#E3E3E3',
						color: theme.palette.text.primary,
					},
				},
			},
			/* CHANGED FOR MUI4 TO MATCH MUI5 */
			MuiAppBar: {
				root: {
					zIndex: 1100,
				},
			},
			/* END: CHANGED FOR MUI4 TO MATCH MUI5 */
		},
	};
}

export const defaultThemes = {
	default: {
		palette: {
			mode: 'light',
			primary: fuseDark,
			secondary: {
				light: lightBlue[400],
				main: lightBlue[600],
				dark: lightBlue[700],
			},
			error: red,
		},
		status: {
			danger: 'orange',
		},
	},
	defaultDark: {
		palette: {
			mode: 'dark',
			primary: fuseDark,
			secondary: {
				light: lightBlue[400],
				main: lightBlue[600],
				dark: lightBlue[700],
			},
			error: red,
		},
		status: {
			danger: 'orange',
		},
	},
};

export function extendThemeWithMixins(obj) {
	const theme = createTheme(adaptV4Theme(obj));
	return {
		border: (width = 1) => ({
			borderWidth: width,
			borderStyle: 'solid',
			borderColor: theme.palette.divider,
		}),
		borderLeft: (width = 1) => ({
			borderLeftWidth: width,
			borderStyle: 'solid',
			borderColor: theme.palette.divider,
		}),
		borderRight: (width = 1) => ({
			borderRightWidth: width,
			borderStyle: 'solid',
			borderColor: theme.palette.divider,
		}),
		borderTop: (width = 1) => ({
			borderTopWidth: width,
			borderStyle: 'solid',
			borderColor: theme.palette.divider,
		}),
		borderBottom: (width = 1) => ({
			borderBottomWidth: width,
			borderStyle: 'solid',
			borderColor: theme.palette.divider,
		}),
	};
}

export function mainThemeVariations(theme) {
	const mainThemeDark = _.merge({}, theme, {
		palette: {
			mode: 'dark',
			background: {
				paper: '#323232',
				default: '#232323',
			},
			secondary: {
				main: '#8e5ef4',
			},
		},
	});
	const mainThemeLight = _.merge({}, theme, {
		palette: {
			mode: 'light',
			background: {
				paper: '#FFFFFF',
				default: '#F7F7F7',
			},
		},
	});

	return {
		mainThemeDark: createTheme(adaptV4Theme(_.merge({}, mainThemeDark, mustHaveThemeOptions(mainThemeDark)))),
		mainThemeLight: createTheme(adaptV4Theme(_.merge({}, mainThemeLight, mustHaveThemeOptions(mainThemeLight)))),
	};
}
